import { Cloudinary } from '@cloudinary/url-gen';
import { fill, scale } from '@cloudinary/url-gen/actions/resize';
import { auto as autoFormat } from '@cloudinary/url-gen/qualifiers/format';
import { auto as autoQuality } from '@cloudinary/url-gen/qualifiers/quality';

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'ddizwgkor';
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const CLOUDINARY_FOLDER = process.env.REACT_APP_CLOUDINARY_FOLDER || '';
const CLOUDINARY_BASE_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}`;
const CLOUDINARY_UPLOAD_URL = `${CLOUDINARY_BASE_URL}/image/upload`;
const CLOUDINARY_DELETE_URL = `${CLOUDINARY_BASE_URL}/delete_by_token`;

const cld = new Cloudinary({
  cloud: {
    cloudName: CLOUDINARY_CLOUD_NAME
  }
});

// Helper function to extract websiteId and sectionId from current URL or React Router params
const getCurrentContext = () => {
  try {
    const pathname = window.location.pathname;
    const pathParts = pathname.split('/').filter(part => part);
    
    console.log('Current pathname:', pathname);
    console.log('Path parts:', pathParts);
    
    let websiteId = null;
    let sectionId = null;
    
    // Try to get from React Router params if available
    try {
      const urlParams = new URLSearchParams(window.location.search);
      websiteId = urlParams.get('websiteId') || websiteId;
      sectionId = urlParams.get('sectionId') || sectionId;
    } catch (e) {
      console.log('No URL params found');
    }
    
    // Look for patterns in URL path
    for (let i = 0; i < pathParts.length; i++) {
      if (pathParts[i] === 'website' && pathParts[i + 1]) {
        websiteId = pathParts[i + 1];
      }
      if (pathParts[i] === 'section' && pathParts[i + 1]) {
        sectionId = pathParts[i + 1];
      }
      if (pathParts[i] === 'gallery' && pathParts[i + 1] && pathParts[i + 2]) {
        websiteId = pathParts[i + 1];
        sectionId = pathParts[i + 2];
      }
    }
    
    // Try to get from global state or context if available
    if (!websiteId || !sectionId) {
      try {
        // Check if there's a global context or state
        if (window.currentWebsiteContext) {
          websiteId = websiteId || window.currentWebsiteContext.websiteId;
          sectionId = sectionId || window.currentWebsiteContext.sectionId;
        }
      } catch (e) {
        console.log('No global context found');
      }
    }
    
    console.log('Final detected context:', { websiteId, sectionId });
    return { websiteId, sectionId };
  } catch (error) {
    console.error('Error extracting context:', error);
    return { websiteId: null, sectionId: null };
  }
};

// Helper function to generate storage keys
const getStorageKeys = (websiteId, sectionId) => {
  return {
    photos: `wedding_Uploads_photos_${websiteId}_${sectionId}`,
    count: `wedding_Uploads_count_${websiteId}_${sectionId}`,
    deleteTokens: `wedding_Uploads_delete_tokens_${websiteId}_${sectionId}`
  };
};

// Helper function to get user uploaded photos from localStorage
const getUserUploadedPhotos = (websiteId, sectionId) => {
  if (!websiteId || !sectionId) return [];
  
  const { photos: storageKey } = getStorageKeys(websiteId, sectionId);
  const storedPhotos = localStorage.getItem(storageKey);
  try {
    const photos = storedPhotos ? JSON.parse(storedPhotos) : [];
    // Normalize stored photos to strings
    const normalized = photos.map(photo => 
      typeof photo === 'string' ? photo : photo.secure_url || ''
    ).filter(url => url);
    return normalized;
  } catch (error) {
    console.error('Error parsing stored photos:', error);
    return [];
  }
};

// Helper function to save uploaded photo to localStorage
const saveUploadedPhoto = (photo, websiteId, sectionId) => {
  const photoUrl = typeof photo === 'string' ? photo : photo?.secure_url;
  const deleteToken = typeof photo === 'object' && photo ? photo.delete_token : null;

  if (!websiteId || !sectionId || !photoUrl) {
    console.log('Skipping localStorage save - missing context:', { websiteId, sectionId, photoUrl });
    return;
  }
  
  try {
    const { photos: photosKey, count: countKey, deleteTokens: deleteTokensKey } = getStorageKeys(websiteId, sectionId);
    
    // Get current photos and add new one
    const currentPhotos = getUserUploadedPhotos(websiteId, sectionId);
    if (!currentPhotos.includes(photoUrl)) {
      const updatedPhotos = [...currentPhotos, photoUrl];
      localStorage.setItem(photosKey, JSON.stringify(updatedPhotos));
      
      // Update count
      const currentCount = parseInt(localStorage.getItem(countKey) || '0', 10);
      localStorage.setItem(countKey, (currentCount + 1).toString());
      
      console.log('Saved uploaded photo to localStorage:', { photoUrl, websiteId, sectionId });
    }

    if (deleteToken) {
      let currentTokens = {};
      try {
        const storedTokens = localStorage.getItem(deleteTokensKey);
        currentTokens = storedTokens ? JSON.parse(storedTokens) : {};
      } catch (error) {
        console.error('Error parsing stored delete tokens:', error);
        currentTokens = {};
      }

      currentTokens[photoUrl] = deleteToken;
      localStorage.setItem(deleteTokensKey, JSON.stringify(currentTokens));
    }
  } catch (error) {
    console.error('Error saving uploaded photo to localStorage:', error);
  }
};

// Main upload function - uses a special parameter to detect wedding gallery context
async function uploadImage(file, websiteId = null, sectionId = null, returnFullObject = null) {
  if (!CLOUDINARY_UPLOAD_PRESET) {
    throw new Error('Missing Cloudinary upload preset. Set REACT_APP_CLOUDINARY_UPLOAD_PRESET.');
  }

  console.log('Uploading file:', file.name, file.size, file.type);

  // Auto-detect context if not provided
  let currentWebsiteId = websiteId;
  let currentSectionId = sectionId;

  if (!currentWebsiteId || !currentSectionId) {
    const context = getCurrentContext();
    currentWebsiteId = currentWebsiteId || context.websiteId;
    currentSectionId = currentSectionId || context.sectionId;
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  // Note: unsigned uploads do not allow return_delete_token.

  if (CLOUDINARY_FOLDER) {
    formData.append('folder', CLOUDINARY_FOLDER);
  }

  if (currentWebsiteId || currentSectionId) {
    const contextParts = [];
    if (currentWebsiteId) contextParts.push(`websiteId=${currentWebsiteId}`);
    if (currentSectionId) contextParts.push(`sectionId=${currentSectionId}`);
    if (contextParts.length) {
      formData.append('context', contextParts.join('|'));
    }
  }

  try {
    const response = await fetch(CLOUDINARY_UPLOAD_URL, {
      method: 'POST',
      body: formData
    });

    console.log('Response status:', response.status);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    console.log('Upload response:', data);

    if (!data.secure_url) {
      throw new Error('Cloudinary upload did not return a secure_url.');
    }

    if (typeof window !== 'undefined') {
      window._lastUploadResponse = data;
    }

    // Save to localStorage if we have valid context
    if (currentWebsiteId && currentSectionId && data.secure_url) {
      saveUploadedPhoto(data, currentWebsiteId, currentSectionId);
    }
    
    // Determine return format
    let shouldReturnFullObject = returnFullObject;
    
    // If not explicitly set, try to detect from call stack (works in development)
    if (shouldReturnFullObject === null) {
      try {
        const stack = new Error().stack;
        shouldReturnFullObject = stack && (
          stack.includes('WeddingPhotoGallery') || 
          stack.includes('storeUploadedPhoto') ||
          stack.includes('handlePhotoUpload')
        );
      } catch (e) {
        shouldReturnFullObject = false;
      }
    }
    
    // If still not determined, check URL as fallback
    if (shouldReturnFullObject === null || shouldReturnFullObject === false) {
      const pathname = window.location.pathname;
      // In production, wedding galleries are often in website viewer, not builder
      shouldReturnFullObject = !pathname.includes('/builder');
    }
    
    console.log('Upload context check:', { 
      pathname: window.location.pathname, 
      returnFullObject,
      shouldReturnFullObject,
      detectionMethod: returnFullObject !== null ? 'parameter' : 'auto'
    });
    
    // Return appropriate format
    if (shouldReturnFullObject) {
      console.log('Returning full object');
      return data;
    } else {
      console.log('Returning URL string');
      return data.secure_url;
    }
  } catch (error) {
    console.error('Error uploading image to Cloudinary:', error);
    throw error;
  }
}

// Function to check if user can delete a photo
const canDeletePhoto = (photoUrl, websiteId = null, sectionId = null) => {
  let currentWebsiteId = websiteId;
  let currentSectionId = sectionId;
  
  if (!currentWebsiteId || !currentSectionId) {
    const context = getCurrentContext();
    currentWebsiteId = currentWebsiteId || context.websiteId;
    currentSectionId = currentSectionId || context.sectionId;
  }
  
  if (!currentWebsiteId || !currentSectionId) return false;
  
  const userPhotos = getUserUploadedPhotos(currentWebsiteId, currentSectionId);
  return userPhotos.includes(photoUrl);
};

// Function to delete a photo
const deletePhoto = async (photoUrl, websiteId = null, sectionId = null) => {
  let currentWebsiteId = websiteId;
  let currentSectionId = sectionId;
  
  if (!currentWebsiteId || !currentSectionId) {
    const context = getCurrentContext();
    currentWebsiteId = currentWebsiteId || context.websiteId;
    currentSectionId = currentSectionId || context.sectionId;
  }
  
  if (!canDeletePhoto(photoUrl, currentWebsiteId, currentSectionId)) {
    throw new Error('You can only delete photos you uploaded');
  }

  try {
    let deleteToken = null;
    if (currentWebsiteId && currentSectionId) {
      const { deleteTokens: deleteTokensKey } = getStorageKeys(currentWebsiteId, currentSectionId);
      try {
        const storedTokens = localStorage.getItem(deleteTokensKey);
        const tokenMap = storedTokens ? JSON.parse(storedTokens) : {};
        deleteToken = tokenMap ? tokenMap[photoUrl] : null;
      } catch (error) {
        console.error('Error parsing stored delete tokens:', error);
      }
    }

    if (deleteToken) {
      const deleteForm = new FormData();
      deleteForm.append('token', deleteToken);
      try {
        const deleteResponse = await fetch(CLOUDINARY_DELETE_URL, {
          method: 'POST',
          body: deleteForm
        });

        if (!deleteResponse.ok) {
          const errorText = await deleteResponse.text();
          console.warn('Cloudinary delete failed:', errorText);
        }
      } catch (error) {
        console.warn('Cloudinary delete request failed:', error);
      }
    } else {
      console.warn('No Cloudinary delete token found for photo. Skipping remote delete.');
    }

    // Remove from localStorage
    if (currentWebsiteId && currentSectionId) {
      const { photos: photosKey, count: countKey, deleteTokens: deleteTokensKey } = getStorageKeys(currentWebsiteId, currentSectionId);
      const userPhotos = getUserUploadedPhotos(currentWebsiteId, currentSectionId);
      const updatedUserPhotos = userPhotos.filter(photo => photo !== photoUrl);
      localStorage.setItem(photosKey, JSON.stringify(updatedUserPhotos));

      if (deleteTokensKey) {
        try {
          const storedTokens = localStorage.getItem(deleteTokensKey);
          const tokenMap = storedTokens ? JSON.parse(storedTokens) : {};
          if (tokenMap && typeof tokenMap === 'object') {
            delete tokenMap[photoUrl];
            localStorage.setItem(deleteTokensKey, JSON.stringify(tokenMap));
          }
        } catch (error) {
          console.error('Error updating delete token map:', error);
        }
      }

      const currentCount = parseInt(localStorage.getItem(countKey) || '0', 10);
      if (currentCount > 0) {
        localStorage.setItem(countKey, (currentCount - 1).toString());
      }
    }

    return true;
  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
};

// Function to get the last upload response (for components that need full object data)
const getLastUploadResponse = () => {
  if (typeof window !== 'undefined' && window._lastUploadResponse) {
    return window._lastUploadResponse;
  }
  return null;
};

export { uploadImage, getUserUploadedPhotos, canDeletePhoto, deletePhoto, saveUploadedPhoto, getLastUploadResponse };

export const getOptimizedImageUrl = (publicId, options = {}) => {
  const image = cld.image(publicId);
  image.format(autoFormat()).quality(autoQuality());
  if (options.format) image.format(options.format);
  if (options.quality) image.quality(options.quality);

  if (options.width || options.height) {
    const resizeAction = options.crop ? fill() : scale();
    if (options.width) resizeAction.width(options.width);
    if (options.height) resizeAction.height(options.height);
    image.resize(resizeAction);
  }

  return image.toURL();
};

export const generateWebsiteId = (websiteName) => {
  return websiteName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

export default cld;
